import {Plain} from '../../../../../../shared/model/plain.type.model';
import {LabelTranslator} from '../../../model/label-translator.model';
import {EmailBlock} from './email-block.model';
import {EMAIL_BLOCK_TYPE} from './email-block-type.model';
import {EMAIL_DEFAULT_BODY_COLOR} from './email-default-body-color.model';
import {EmailRow} from './email-row.model';
import {toEmailRow} from './to-email-row.model';

/**
 * Le corps de l'e-mail : ses sections, dans l'ordre où le destinataire les lira, et le fond sur
 * lequel elles se posent.
 *
 * C'est ce que le back range sous `content`, en JSON libre — d'où la normalisation ici plutôt
 * qu'un schéma côté serveur.
 */
export class EmailContent {
    public bodyColor: string;
    public rows: EmailRow[];

    /** Absent d'un média que le serveur vient de créer : le corps arrive alors vide, jamais `null`. */
    constructor(data?: Plain<EmailContent> | null) {
        // L'API omet les champs non renseignés : on normalise en structures vides, jamais en null.
        this.bodyColor = data?.bodyColor || EMAIL_DEFAULT_BODY_COLOR;
        this.rows = (data?.rows ?? []).map(toEmailRow);
    }

    /**
     * Le corps, ses libellés de gabarit traduits. Rendu à l'identique quand aucune de ses sections
     * n'en portait : c'est à cela que le store reconnaît qu'il n'y a rien à enregistrer.
     */
    public readonly withTranslatedTexts = (translate: LabelTranslator): EmailContent => {
        const rows = this.rows.map((row) => row.withTranslatedTexts(translate));
        if (rows.every((row, rank) => row === this.rows[rank])) return this;

        return new EmailContent({...this, rows});
    };

    public readonly withBodyColor = (bodyColor: string): EmailContent => new EmailContent({...this, bodyColor});

    /** Insère une section au rang voulu ; un rang hors des bornes est ramené dedans. */
    public readonly withRowAdded = (row: EmailRow, index: number): EmailContent => {
        const rows = [...this.rows];
        rows.splice(this.boundedRank(index), 0, row);

        return new EmailContent({...this, rows});
    };

    public readonly withRow = (index: number, row: EmailRow): EmailContent =>
        new EmailContent({...this, rows: this.rows.map((current, rank) => (rank === index ? row : current))});

    public readonly withRowRemoved = (index: number): EmailContent =>
        new EmailContent({...this, rows: this.rows.filter((_, rank) => rank !== index)});

    public readonly withRowMoved = (from: number, to: number): EmailContent => {
        if (!this.rows[from]) return this;

        const rows = [...this.rows];
        const [moved] = rows.splice(from, 1);
        rows.splice(this.boundedRank(to), 0, moved);

        return new EmailContent({...this, rows});
    };

    public readonly blockAt = (rowIndex: number, columnIndex: number, blockIndex: number): EmailBlock | undefined =>
        this.rows[rowIndex]?.columns[columnIndex]?.content[blockIndex];

    /**
     * L'e-mail porte-t-il déjà un bloc de ce type ?
     *
     * Le bouton de collecte est le seul concerné : l'e-mail n'en emporte qu'un, la palette éteint
     * donc sa carte dès qu'il est posé — et son absence retient la publication.
     */
    /**
     * L'e-mail porte-t-il un emplacement d'image resté vide ?
     *
     * Le serveur refuse de publier un e-mail dans cet état (`no_image_id`) : l'emplacement partirait
     * au destinataire en cadre vide. On le dit avant, plutôt que d'attendre son refus.
     */
    public readonly hasEmptyImage = (): boolean =>
        this.rows.some((row) =>
            row.columns.some((column) =>
                column.content.some((block) => block.type === EMAIL_BLOCK_TYPE.IMAGE && !block.options.imageId)
            )
        );

    public readonly hasBlockOfType = (type: EMAIL_BLOCK_TYPE): boolean =>
        this.rows.some((row) => row.columns.some((column) => column.content.some((block) => block.type === type)));

    /** Un dépôt en plein canevas ne doit pas dépendre du pixel visé. */
    private readonly boundedRank = (index: number): number => Math.min(Math.max(index, 0), this.rows.length);
}
